<template>
  <div
    id="shipping-calculator-drawer"
    data-cs-capture>
    <div
      v-if="shippingRestrictions.restricted"
      class="bg-paper-1 text-red p-4 flex mt-4 -mb-4">
      <svg-icon
        class="flex-no-shrink mt-1"
        height="12px"
        width="15px"
        name="warning" />
      <div class="ml-3 restricted-shipping-text-container">
        <h3 class="typeset-7">
          <strong>{{ t("shipping-restrictions.pdp.heading") }}</strong>
        </h3>
        <p class="typeset-7">{{ shippingRestrictionSubheading }}</p>
      </div>
    </div>

    <teleport to="#end-of-body">
      <drawer
        :transition-from-bottom="smallScreen"
        :is-open="isOpen"
        @close-drawer="isOpen = false">
        <template #header>
          <h2 class="typeset-3 mb-4">
            {{ t("shipping-calculator.delivery-options") }}
          </h2>
        </template>
        <template #content>
          <shipping-calculator
            :id="productId"
            :delayed="delayed"
            :countries="countries"
            :regions="regions"
            :product-type="productType"
            :default-country="country || defaultCountry"
            :default-region="defaultRegion"
            :quantity="quantity" />
          <div class="py-6 w-full flex justify-center">
            <a
              href="#"
              class="btn btn-large btn-block btn-primary"
              @click.prevent="isOpen = false">
              Close
            </a>
          </div>
        </template>
      </drawer>
    </teleport>
  </div>
</template>

<script>
import Drawer from "@/app/javascript/components/shared/Drawer.vue";
import SvgIcon from "@/app/javascript/components/shared/SvgIcon.vue";
import api from "@/app/javascript/api/shipping/calculator";

import ShippingCalculator from "./ShippingCalculator.vue";

export default {
  name: "ShippingCalculatorDrawer",
  components: {
    Drawer,
    ShippingCalculator,
    SvgIcon
  },
  props: {
    productId: {
      type: String,
      required: true
    },
    defaultCountry: {
      type: String,
      required: true
    },
    productType: {
      type: String,
      required: true
    },
    standardQuantity: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      isOpen: false,
      tomorrow: null,
      seconds: null,
      countries: null,
      regions: null,
      delayed: null,
      showButton: false,
      shippingRestrictions: {
        restricted: false,
        countryName: ""
      },
      smallScreen: false,
      quantity: "1",
      weddingPickerObserver: null,
      qtyPickerObserver: null,
      defaultRegion: null,
      country: null
    };
  },
  computed: {
    deliveryInfoTitle() {
      return this.delayed
        ? this.t("product.delivery-delayed")
        : this.t("product.delivery-info");
    },
    deliveryInfoPrompt() {
      return this.productType === "StockItem"
        ? this.t("product.stock-items.delivery-info-prompt")
        : this.t("product.products.delivery-info-prompt");
    },
    shippingRestrictionSubheading() {
      if (!this.shippingRestrictions.countryName)
        return this.t("shipping-restrictions.pdp.subheading.no-country");

      return this.t("shipping-restrictions.pdp.subheading.with-country", {
        country: this.shippingRestrictions.countryName
      });
    }
  },
  mounted() {
    const xsQuery = window.matchMedia("(max-width: 479px)");
    xsQuery.addListener(this.xsScreenSize);
    this.xsScreenSize(xsQuery);

    this.quantity = this.standardQuantity;

    this.setupRegion();
    this.setupCalculatorData();
    this.setupProductShippingRestrictions();
    this.setupWeddingQtyPicker();
    this.setupQtyPicker();
    this.setupOpenDrawerTrigger();
  },
  beforeUnmount() {
    this.clearWeddingPickerEvent();
    this.clearQtyPickerEvent();
  },
  methods: {
    setupOpenDrawerTrigger() {
      window.addEventListener("open-shipping-calculator", () => {
        this.isOpen = true;
      });
    },
    clearWeddingPickerEvent() {
      if (this.weddingPickerObserver) {
        this.weddingPickerObserver.disconnect();
        this.weddingPickerObserver = null;
      }
    },
    clearQtyPickerEvent() {
      if (this.qtyPickerObserver) {
        this.qtyPickerObserver.disconnect();
        this.qtyPickerObserver = null;
      }
    },
    setupWeddingQtyPicker() {
      const weddingPickers = document.querySelectorAll(
        ".js-large-quantity-picker-mobile, .js-large-quantity-picker-desktop"
      );

      if (weddingPickers.length) {
        this.weddingPickerObserver = new MutationObserver(mutations => {
          mutations.forEach(mutation => {
            if (
              mutation.type === "attributes" &&
              mutation.target.classList.contains("selected")
            ) {
              this.handleQuantityChange(
                parseInt(mutation.target.dataset.qty, 10)
              );
            }
          });
        });

        weddingPickers.forEach(picker => {
          this.weddingPickerObserver.observe(picker, {
            attributes: true,
            attributeFilter: ["class"],
            childList: true,
            subtree: true
          });
        });
      }
    },
    setupQtyPicker() {
      const qtyPicker = document.getElementById("js-listing-page-qty-picker");
      if (qtyPicker) {
        this.qtyPickerObserver = new MutationObserver(mutations => {
          mutations.forEach(mutation => {
            if (mutation.type === "attributes" && mutation.target.value) {
              this.handleQuantityChange(mutation.target.value);
            }
          });
        });

        this.qtyPickerObserver.observe(qtyPicker, {
          attributes: true,
          attributeFilter: ["value"]
        });
      }
    },
    setupCalculatorData() {
      api
        .show(
          this.productId,
          this.country || this.defaultCountry,
          null,
          this.quantity,
          this.defaultRegion
        )
        .then(response => {
          this.setProperties(response);
          this.htmlChanged();
        });
    },
    handleQuantityChange(qty) {
      this.quantity = qty;

      api
        .show(this.productId, this.country || this.defaultCountry, null, qty)
        .then(this.setProperties);
    },
    setupProductShippingRestrictions() {
      const productPage = document.getElementById("product-page");
      this.shippingRestrictions.restricted = productPage.classList.contains(
        "shipping-restricted"
      );
      this.shippingRestrictions.countryName = productPage.dataset.geoCountry;
    },
    setupRegion() {
      const productPage = document.getElementById("product-page");
      this.defaultRegion = productPage.dataset.geoRegion;
      this.country = productPage.dataset.geoCountryIso;
    },
    setProperties({ data }) {
      this.tomorrow = data.tomorrow;
      this.seconds = data.seconds_to_cutoff;
      this.countries = data.countries;
      this.regions = data.regions;
      this.delayed = !!data.delayed;
      window.postMessage({ type: "shippingTimesSet", shippingInfo: data }, "*");
    },
    htmlChanged() {
      const htmlChanged = new Event("htmlChanged");
      window.dispatchEvent(htmlChanged);
    },
    xsScreenSize(mediaQuery) {
      if (mediaQuery.matches) {
        this.smallScreen = true;
      } else {
        this.smallScreen = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.restricted-shipping-text-container {
  p {
    line-height: 22px;
  }
}
</style>
